/**
 * itemEffectSystem.ts — Visual + logical tracking for passive item effects
 *
 * Manages: poison clouds (Diarrhea Gas), burst explosions (NickMerc Jerk,
 * Morning Wood, Big Brain XP, Bubble Wrap reflect), and the Bubble Wrap
 * invulnerability flash.
 *
 * IMPORTANT: This file intentionally has NO imports from other game systems
 * to avoid circular dependency chains.
 * - Damage is applied via drainAoeDamageRequests() polled by enemyAdapter.
 * - Poisoned-enemy queries use isPositionPoisoned() called from weaponAdapter.
 * - Rendering uses its own world→screen conversion (like renderLootOrbs).
 * - BETA 20.72 — './types' is the one allowed exception (leaf module, shared
 *   types + spawnParticles only — same precedent as the './debugState' import).
 */

// BETA 20.72 — proc-signature particles. Every item proc used to funnel
// through ONE expanding-circle burst (only the hex changed). Dylan: "i want
// it to look epic and all be different so you can tell which weapon or attack
// or passive is doing what." Each proc now spawns its own particle signature
// into a module-local array (rendered ON TOP of enemies — engine's
// state.particles pass draws UNDER them, so we can't reuse it).
import { spawnParticles, type Particle, type ParticleOpts } from './types';
import { tuneOf } from './bunchaBullshiiii';   // BETA 21.78 — editable R17 NickMerc splash%

// ─── Types ───────────────────────────────────────────────────────────────────

interface PoisonCloud {
  x: number; y: number;
  radius: number;
  lifetime: number;      // seconds remaining
  maxLifetime: number;
  dps: number;           // damage per second per enemy in range
  tickTimer: number;     // time until next damage tick
  readonly tickInterval: number;
}

interface BurstEffect {
  x: number; y: number;
  maxRadius: number;
  currentRadius: number;
  lifetime: number;
  readonly maxLifetime: number;
  readonly color: string;
  readonly glowColor: string;
  readonly damage: number;   // total AoE damage on first tick (0 = visual only)
  hasDealtDamage: boolean;
  // BETA 20.72 — true = burst is a DAMAGE CARRIER only (the one-shot AoE
  // request still fires in updateItemEffectVisuals) but the generic expanding
  // circle is NOT drawn — the proc's particle signature replaces it.
  noRing?: boolean;
}

export interface AoeDamageRequest {
  x: number; y: number;
  radius: number;
  damage: number;
  sourceItemId?: string;   // BETA 13.23: attribute item-driven AoE damage in CSV telemetry
  // BETA 23.6x — CO-OP PHASE 2 part C (§3.3): roster PlayerId ('p1'..'p4') that
  // OWNS this AoE (whose item fired it). Stamped at enqueue with the machine-
  // local player — items run in a local-player context today; per-owner
  // threading through the spawn entry points (cloud/rocket/T-bag carrying their
  // owning player) lands in part D with the items' own per-player split.
  // Consumer: enemyAdapter's drain loop → damageEnemy opts.sourcePlayerId.
  ownerPlayerId?: string;
}

// Lightweight enemy ref passed in from engine each frame (no circular import)
interface EnemyRef {
  id: number | string;
  x: number;
  y: number;
  alive: boolean;
}

interface AnusRocket {
  x: number; y: number;
  vx: number; vy: number;     // current velocity
  targetId: number | string | null;    // ID of homed enemy
  damage: number;
  stacks: number;
  lifetime: number;           // seconds before self-destruct
  angle: number;              // current facing angle in radians
  speed: number;              // world units/sec
}

interface TBagEntity {
  x: number; y: number;
  angle: number;              // orbit angle (radians)
  orbitRadius: number;
  orbitSpeed: number;         // radians/sec
  state: 'orbit' | 'dash' | 'returning';
  dashTarget: { x: number; y: number } | null;
  dashCooldown: number;       // seconds between dashes
  dashTimer: number;
  damage: number;
  stacks: number;
}

// ─── Module State ─────────────────────────────────────────────────────────────

// BETA 18.18 — Hard caps to prevent the "FPS dies when dominating" pattern.
// Poison clouds and burst effects both loop O(effects × enemies) per frame
// for damage/render. Diarrhea Gas fires on every damage event (uncapped
// proc) — at high enemy density with reactive builds you can spawn dozens
// per second. NickMerc Jerk crit splash + Big Brain XP burst + Bubble Wrap
// reflect all push into _bursts. FIFO eviction keeps these bounded.
const MAX_CLOUDS = 20;
const MAX_BURSTS = 40;

let _clouds: PoisonCloud[] = [];
let _bursts: BurstEffect[] = [];
let _aoeRequests: AoeDamageRequest[] = [];
// BETA 23.6x — CO-OP PHASE 2 part C (§3.3): the machine-local player id, stamped
// onto every AoE damage request at enqueue (see AoeDamageRequest.ownerPlayerId).
// This file is a LEAF module — no game-system imports allowed — so the id is
// PUSHED in: enemyAdapter.update() syncs it from GameState.localPlayerId every
// frame. Default 'p1' = the only local player in SP → byte-identical stamps.
let _localOwnerPlayerId = 'p1';
export function setItemEffectLocalPlayerId(id: string): void {
  _localOwnerPlayerId = id;
}
let _bubbleWrapInvulnTimer = 0;  // > 0 = player is in bubble-wrap invuln window
let _rockets: AnusRocket[] = [];

// BETA 21.69 — rocket VFX (Dylan): a realistic billowing SMOKE TRAIL behind the rocket +
// an ember-FIRE burst at the explosion (same look as the Big Mac/Tacos fire). Self-contained
// so it doesn't reach into the giant engine.ts fire system.
interface SmokePuff { x: number; y: number; age: number; max: number; r0: number; vx: number; vy: number; gray: number; }
let _rocketSmoke: SmokePuff[] = [];
interface RocketFire { x: number; y: number; age: number; max: number; radius: number; seed: number; }
let _rocketFires: RocketFire[] = [];
// BETA 21.96 — bespoke rocket EXPLOSION (Dylan: "no cool explosion on the rocket"). A bright
// flash + an expanding shockwave ring + flying ember sparks, layered on top of the existing
// ember-fire. Kept tasteful in size (17.96: VFX must not out-scale the gameplay element) —
// it's the DETAIL that sells the blast, not raw size.
interface RocketBlast { x: number; y: number; age: number; max: number; radius: number; sparks: { ang: number; sp: number; len: number }[]; }
let _rocketBlasts: RocketBlast[] = [];
function _spawnRocketBlast(x: number, y: number, radius: number): void {
  const n = 9 + Math.floor(radius / 14);
  const sparks: { ang: number; sp: number; len: number }[] = [];
  for (let i = 0; i < n; i++) sparks.push({ ang: (i / n) * Math.PI * 2 + Math.random() * 0.6, sp: radius * (1.3 + Math.random() * 1.3), len: 6 + Math.random() * 11 });
  _rocketBlasts.push({ x, y, age: 0, max: 0.46, radius, sparks });
}
// Flame-tongue table for the explosion ember-fire (quadratic-curve licks that flicker).
const _ROCKET_FIRE_TONGUES = [
  { ox: -0.55, len: 0.95, w: 3.2, lean:  0.10, ph: 0.0, color: '#ff7a18' },
  { ox: -0.22, len: 1.28, w: 3.8, lean: -0.06, ph: 1.3, color: '#ffd23b' },
  { ox:  0.08, len: 1.48, w: 4.2, lean:  0.04, ph: 2.5, color: '#ffe98a' },
  { ox:  0.38, len: 1.12, w: 3.4, lean: -0.10, ph: 3.7, color: '#ff8c1a' },
  { ox:  0.62, len: 0.82, w: 2.8, lean:  0.12, ph: 4.9, color: '#ff5a00' },
];
/** Spawn a soft billow of smoke at the rocket's tail (world coords). */
function _spawnRocketSmoke(x: number, y: number): void {
  if (_rocketSmoke.length > 220) return; // hard cap (perf)
  _rocketSmoke.push({
    x, y, age: 0, max: 0.55 + Math.random() * 0.45, r0: 3 + Math.random() * 3,
    vx: (Math.random() - 0.5) * 14, vy: (Math.random() - 0.5) * 14 - 6, // slight rise + spread
    gray: 150 + Math.floor(Math.random() * 50),
  });
}
/** Ember-fire burst at an explosion (screen coords + screen radius). Same style as Big Mac. */
function _drawRocketFire(ctx: CanvasRenderingContext2D, sx: number, sy: number, screenR: number, alpha: number, time: number, seed: number): void {
  if (alpha < 0.03) return;
  ctx.lineCap = 'round';
  ctx.globalAlpha = alpha * 0.30; ctx.fillStyle = '#7a1d00';
  ctx.beginPath(); ctx.ellipse(sx, sy, screenR, screenR * 0.70, 0, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = alpha * 0.55; ctx.fillStyle = '#c23a00';
  ctx.beginPath(); ctx.ellipse(sx, sy, screenR * 0.55, screenR * 0.40, 0, 0, Math.PI * 2); ctx.fill();
  const flickBase = time * 9 + seed * 1.93;
  ctx.globalAlpha = alpha * 0.92;
  for (const tg of _ROCKET_FIRE_TONGUES) {
    const flick = Math.sin(flickBase + tg.ph);
    const h = screenR * tg.len * (0.72 + 0.28 * flick);
    const bx = sx + tg.ox * screenR, by = sy + screenR * 0.1;
    ctx.strokeStyle = tg.color; ctx.lineWidth = tg.w;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.quadraticCurveTo(bx - flick * screenR * 0.14, by - h * 0.55, bx + tg.lean * screenR + flick * screenR * 0.12, by - h);
    ctx.stroke();
  }
  ctx.lineCap = 'butt'; ctx.lineWidth = 1; ctx.globalAlpha = 1.0;
}
let _tbag: TBagEntity | null = null;
let _enemyRefs: EnemyRef[] = [];

// ─── BETA 20.72 — Proc-Signature Particle Harness ────────────────────────────
// Module-local particle pool fed by spawnParticles (types.ts applies the
// graphics density multiplier + MAX_PARTICLES cap for free). Physics ticks
// in updateItemEffectVisuals (exact mirror of engine.ts particle motion);
// rendering happens in renderItemEffects with its own world→screen pass.

let _procParticles: Particle[] = [];

// Stagger queue — spawnParticles has no delay param, but the signatures need
// staggered sub-bursts (thorn tips lagging behind, ripple waves, glitch
// strobes, concentric exhaust rings). FIFO-capped like _bursts.
interface PendingSpawn {
  delay: number;          // seconds until this sub-burst fires
  x: number; y: number;
  color: string;
  count: number;
  opts: ParticleOpts;
}
const MAX_PENDING_SPAWNS = 24;
let _pendingSpawns: PendingSpawn[] = [];

function _queueSpawn(delay: number, x: number, y: number, color: string, count: number, opts: ParticleOpts): void {
  if (_pendingSpawns.length >= MAX_PENDING_SPAWNS) _pendingSpawns.shift();
  _pendingSpawns.push({ delay, x, y, color, count, opts });
}

// Hoisted spawn options — one const per sub-burst so proc events allocate
// nothing. Queued (delayed) spawns MUST only reference static objects; the
// two R08 exhaust opts mutate `.dir` per hit but always spawn synchronously.

// R17 NickMerc Jerk — orange splash droplets, arcing down, 3-wave ripple
const _R17_DROPLETS_1: ParticleOpts = { shape: 'circle', sizeMin: 2, sizeMax: 4, speedMin: 90, speedMax: 200, gravity: 340, lifeMin: 0.3, lifeMax: 0.5, fadePow: 1.5 };
const _R17_DROPLETS_2: ParticleOpts = { shape: 'circle', sizeMin: 1.5, sizeMax: 3.5, speedMin: 70, speedMax: 160, gravity: 340, lifeMin: 0.28, lifeMax: 0.45, fadePow: 1.5 };
const _R17_DROPLETS_3: ParticleOpts = { shape: 'circle', sizeMin: 1.5, sizeMax: 3, speedMin: 50, speedMax: 120, gravity: 340, lifeMin: 0.25, lifeMax: 0.4, fadePow: 1.5 };

// R09 Peanut's Big Brain — cyan knowledge sparkle-rays (stars + streaks)
const _R09_STARS: ParticleOpts = { shape: 'star', spin: 6, spinRandomSign: true, speedMin: 60, speedMax: 160, sizeMin: 2.5, sizeMax: 4, lifeMin: 0.3, lifeMax: 0.5 };
const _R09_RAYS:  ParticleOpts = { shape: 'spark', stretch: 7, speedMin: 160, speedMax: 300, sizeMin: 2, sizeMax: 3.5, lifeMin: 0.25, lifeMax: 0.45 };

// R10 Morning Wood — fast light wooden thorn spray + red-hot tips lagging
const _R10_THORN_SHARDS: ParticleOpts = { shape: 'shard', speedMin: 140, speedMax: 240, spin: 9, spinRandomSign: true, gravity: 120, sizeMin: 3, sizeMax: 5, lifeMin: 0.3, lifeMax: 0.45 };
const _R10_REDHOT_TIPS:  ParticleOpts = { shape: 'shard', sizeMin: 1.5, sizeMax: 2.5, speedMin: 180, speedMax: 280, spin: 12, spinRandomSign: true, lifeMin: 0.2, lifeMax: 0.35 };

// R03 Wyfe Beater — deliberately ANTI-R10: fewer / heavier / slower iron chunks
const _R03_IRON_SPIKES: ParticleOpts = { shape: 'shard', speedMin: 60, speedMax: 130, sizeMin: 4, sizeMax: 6, gravity: 260, spin: 3, spinRandomSign: true, lifeMin: 0.45, lifeMax: 0.65 };
const _R03_REDHOT_TIPS: ParticleOpts = { shape: 'shard', sizeMin: 1.5, sizeMax: 2.5, speedMin: 90, speedMax: 160, spin: 5, spinRandomSign: true, lifeMin: 0.3, lifeMax: 0.45 };

// R06 Bubble Wrap — rising bubbles (negative gravity) that POP at end of life
// (pop handled by the local bubble renderer below) + a pale shock ring.
// fadePow 1 keeps bubbles visible right up to the pop window.
const _R06_BUBBLES:    ParticleOpts = { shape: 'bubble', sizeMin: 2.5, sizeMax: 7, speedMin: 25, speedMax: 90, gravity: -120, drag: 0.97, lifeMin: 0.45, lifeMax: 0.8, fadePow: 1 };
const _R06_SHOCK_RING: ParticleOpts = { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 4, sizeMax: 5, lifeMin: 0.35, lifeMax: 0.35 };

// R08 Anus Rocket — backward exhaust cone (dir mutated per hit, spawned
// synchronously only) + smoke donuts rolling out behind it.
// BETA 20.78 — the 3 concentric shock rings that used to follow the cone
// duplicated the chick-magnet ring motif (identity law: no two effects share
// a look). Each ring is now a DONUT of gray-brown smoke motes: the spawn
// site places single motes around a circle (hole in the middle = donut, not
// a blob) and they rise on negative gravity while drifting apart.
const _R08_EXHAUST_ORANGE: ParticleOpts = { shape: 'spark', dir: 0, spread: 0.5, stretch: 6, speedMin: 180, speedMax: 320, sizeMin: 2, sizeMax: 4, lifeMin: 0.2, lifeMax: 0.35 };
const _R08_EXHAUST_YELLOW: ParticleOpts = { shape: 'spark', dir: 0, spread: 0.35, stretch: 5, speedMin: 240, speedMax: 380, sizeMin: 1.5, sizeMax: 3, lifeMin: 0.15, lifeMax: 0.3 };
const _R08_SMOKE_PUFF: ParticleOpts = { shape: 'mote', gravity: -55, drag: 0.96, speedMin: 6, speedMax: 26, sizeMin: 2.5, sizeMax: 4.5, lifeMin: 0.45, lifeMax: 0.75 };

// R16 Nutty T-Bag — dive-crater: dark center puff + dirt chunks that arc
// and FALL (heavy gravity) + ground shock ring
const _R16_CRATER_CENTER: ParticleOpts = { shape: 'mote', speedMin: 10, speedMax: 50, sizeMin: 3, sizeMax: 5, lifeMin: 0.25, lifeMax: 0.4 };
const _R16_DIRT_CHUNKS:   ParticleOpts = { shape: 'shard', speedMin: 90, speedMax: 190, gravity: 420, spin: 7, spinRandomSign: true, sizeMin: 3, sizeMax: 5, lifeMin: 0.35, lifeMax: 0.55 };
const _R16_GROUND_RING:   ParticleOpts = { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 5, sizeMax: 6, lifeMin: 0.3, lifeMax: 0.3 };

// ─── Sprite Loading (chroma-key green removal) ───────────────────────────────

let _rocketImg: HTMLImageElement | null = null;
let _tbagImg:   HTMLImageElement | null = null;

function loadChromaKeySprite(url: string): HTMLImageElement {
  const result = new Image();
  const raw = new Image();
  raw.crossOrigin = 'anonymous';
  raw.onload = () => {
    // BETA 17.42 — Dylan reported "tea bag = brown rectangle in game".
    // chroma-key pipeline wrapped in try/catch; falls back to raw sprite
    // on any error.
    //
    // BETA 19.3 — DEFER the heavy canvas work via setTimeout(0). Top
    // suspect for the 12-17s heartbeat freezes on the ROG Ally was this
    // synchronous canvas pipeline running inside the image onload
    // callback while the main thread was already busy. Wrapping in
    // setTimeout(0) yields back to the event loop first, then runs the
    // canvas work on a later tick. Also adds timing logs so we can see
    // in DevTools console which sprite took how long.
    setTimeout(() => {
      const _t0 = performance.now();
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = raw.naturalWidth;
        canvas.height = raw.naturalHeight;
        const c2 = canvas.getContext('2d');
        if (!c2) { result.src = url; return; }
        c2.drawImage(raw, 0, 0);
        const imgData = c2.getImageData(0, 0, canvas.width, canvas.height);
        const d = imgData.data;
        for (let i = 0; i < d.length; i += 4) {
          // Pure chroma-green: G >> 200, R < 80, B < 80
          if (d[i + 1] > 200 && d[i] < 80 && d[i + 2] < 80) d[i + 3] = 0;
        }
        c2.putImageData(imgData, 0, 0);
        result.src = canvas.toDataURL('image/png');
      } catch (e) {
        console.warn('[chromaKey] failed for', url, '— falling back to raw sprite:', e);
        result.src = url;
      }
      const _t1 = performance.now();
      // BETA 19.3 — flag long chromaKey passes (>200ms is a freeze risk).
      if (_t1 - _t0 > 200) {
        console.warn(`[chromaKey] SLOW ${(_t1 - _t0).toFixed(0)}ms for ${url} (${raw.naturalWidth}×${raw.naturalHeight})`);
      }
      result.decode?.().catch(() => { /* pre-decode best-effort */ });
    }, 0);
  };
  raw.onerror = () => {
    console.error('[chromaKey] image load FAILED for', url);
    result.src = url; // last-ditch — let the browser try via standard path
  };
  raw.src = url;
  return result;
}

// BETA 14.78 — Diarrhea Gas ground sprite. User dropped a stylized gas-cloud
// PNG into client/public/items/item_diarrhea_gas_f4.png and asked: "duplicate
// this sprite to go through 3 of these and just rotate each sprite so when it
// flickers through it looks animated. put the sprite at 30% opacity."
// Single sprite + 3 rotation phases via canvas transform — no need for
// 3 separate PNG files.
let _diarrheaGasImg: HTMLImageElement | null = null;

/** Call once at init — preloads Anus Rocket + Nutty T-Bag + Diarrhea Gas sprites. */
export function preloadItemSprites(): void {
  // BETA 20.34 — Dylan: "nutty t bag doesn't work anymore" (bag never appears).
  // Root cause: the 20.13 asset-optimization pass re-encoded these to proper
  // TRANSPARENT PNGs (corner alpha = 0), but they were still run through the
  // legacy chroma-key — which, built for the OLD opaque backgrounds, now keys
  // out the sprites' own dark pixels → fully invisible (and the brown fallback
  // never shows because the image technically "loaded"). Both sprites already
  // have alpha, so load them straight. Anus Rocket (R08) had the same latent bug.
  _rocketImg = new Image(); _rocketImg.src = '/props/items/anus_rocket.png';
  _tbagImg   = new Image(); _tbagImg.src   = '/props/items/nutty_tbag.png';
  // BETA 14.78 — Diarrhea Gas ground cloud sprite
  _diarrheaGasImg = new Image();
  _diarrheaGasImg.src = '/items/item_diarrhea_gas_f4.png';
}
export function getDiarrheaGasImg(): HTMLImageElement | null { return _diarrheaGasImg; }

// ─── Internal: Nearest-enemy helper ──────────────────────────────────────────

function _nearestEnemy(x: number, y: number, excludeId: string | null): EnemyRef | null {
  let best: EnemyRef | null = null;
  let bestD2 = Infinity;
  for (const e of _enemyRefs) {
    if (!e.alive) continue;
    if (e.id === excludeId) continue;
    const dx = e.x - x; const dy = e.y - y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) { bestD2 = d2; best = e; }
  }
  return best;
}

// ─── Public: Spawn Functions ──────────────────────────────────────────────────

/** Diarrhea Gas (R11): spawn poison cloud at player position on taking damage.
 *  BETA 14.65 — scales with playerDamageMult (player.damageBonus / 100 + 1)
 *  + attackArea bonus (radius).
 *  BETA 16.30 — PSBOT cycle-30e Phase 2: optional dimension contributions
 *  for radius / dps / lifetime so R11 dimension picks scale the cloud.
 *  Caller passes pickedDimensions snapshot from slot. */
export function spawnPoisonCloud(
  x: number,
  y: number,
  stacks: number,
  dmgMult: number = 1.0,
  areaMult: number = 1.0,
  dimensions?: {
    radiusBonus?: number;  // wu added to radius
    dpsBonus?: number;     // flat dps added
    lifetimeBonus?: number; // sec added
  },
): void {
  const lifetime = tuneOf('R11', 'cloudLifetime', 3.0) + (dimensions?.lifetimeBonus ?? 0);
  // BETA 18.18 — FIFO cap. Diarrhea Gas procs on every damage event with no
  // cooldown — under heavy enemy fire you could spawn 20+ clouds/sec.
  if (_clouds.length >= MAX_CLOUDS) _clouds.shift();
  _clouds.push({
    x, y,
    radius: ((tuneOf('R11', 'cloudRadiusBase', 80) + stacks * tuneOf('R11', 'cloudRadiusPerStack', 20)) + (dimensions?.radiusBonus ?? 0)) * areaMult,
    lifetime,
    maxLifetime: lifetime,
    dps: (tuneOf('R11', 'cloudDpsPerStack', 8) * stacks * dmgMult) + (dimensions?.dpsBonus ?? 0),
    tickTimer: 0,
    tickInterval: tuneOf('R11', 'cloudTickInterval', 0.25),
  });
}

/** NickMerc Jerk (R17): crit explosion at hit position */
export function spawnCritExplosion(x: number, y: number, damage: number, stacks: number): void {
  const radius = tuneOf('R17', 'radiusBase', 100) + stacks * tuneOf('R17', 'radiusPerStack', 25);   // BETA 21.79 — editable splash size
  // BETA 18.18 — FIFO cap. NickMerc Jerk fires on every crit at high attack
  // speed builds with 5 stacks; could push hundreds of bursts/sec.
  if (_bursts.length >= MAX_BURSTS) _bursts.shift();
  _bursts.push({
    x, y,
    maxRadius: radius,
    currentRadius: 0,
    lifetime: 0.38,
    maxLifetime: 0.38,
    color: '#ff8800',
    glowColor: '#ff4400',
    damage: damage * tuneOf('R17', 'splashPct', 0.5) * stacks,   // BETA 21.78 — editable splash fraction (× copies)
    hasDealtDamage: false,
    noRing: true,  // BETA 20.72 — burst kept as the damage carrier; circle hidden
  });
  // BETA 20.72 — R17 signature: orange splash-droplet ripple (was shared
  // circle). Droplets arc down under gravity like a splash; two staggered
  // sub-bursts roll outward slower = ripple wave.
  spawnParticles(_procParticles, x, y, '#ff8800', 5, _R17_DROPLETS_1);
  _queueSpawn(0.06, x, y, '#ff9933', 4, _R17_DROPLETS_2);
  _queueSpawn(0.12, x, y, '#ffaa44', 3, _R17_DROPLETS_3);
}

/** Big Brain (R09): XP orb explosion at pickup position.
 *  BETA 14.65 — Now scales with player damage + attack area. Was hardcoded
 *  `damage: 12 * stacks`. */
export function spawnXpBurst(x: number, y: number, stacks: number, dmgMult: number = 1.0, areaMult: number = 1.0): void {
  // BETA 18.18 — FIFO cap shared across all _bursts producers.
  if (_bursts.length >= MAX_BURSTS) _bursts.shift();
  _bursts.push({
    x, y,
    maxRadius: (80 + stacks * 20) * areaMult,
    currentRadius: 0,
    lifetime: 0.30,
    maxLifetime: 0.30,
    color: '#44ffee',
    glowColor: '#00ccff',
    damage: 12 * stacks * dmgMult,
    hasDealtDamage: false,
  });
}

/** Big Brain (R09): cyan knowledge sparkle-rays.
 *  BETA 20.72 — VISUAL ONLY, no damage. R09 damage runs via the engine's
 *  _bigBrainTimer hazard tick (since 20.57) — spawnXpBurst above is orphaned
 *  and calling it from the tick would ADD a second damage source. Engine
 *  calls this once per orb that landed damage (tick already throttled to
 *  0.45s). Diagonal star sparkles + thin spark streaks = XP energy. */
export function spawnBigBrainSparkle(x: number, y: number): void {
  spawnParticles(_procParticles, x, y, '#44ffee', 3, _R09_STARS);
  spawnParticles(_procParticles, x, y, '#aaffff', 3, _R09_RAYS);
}

/** Morning Wood (R10): thorns AoE explosion at enemy hit position */
export function spawnMorningWoodExplosion(x: number, y: number, thornDamage: number, stacks: number): void {
  // BETA 18.18 — FIFO cap shared across all _bursts producers.
  if (_bursts.length >= MAX_BURSTS) _bursts.shift();
  _bursts.push({
    x, y,
    maxRadius: tuneOf('R10', 'radiusBase', 90) + stacks * tuneOf('R10', 'radiusPerStack', 22),
    currentRadius: 0,
    lifetime: 0.35,
    maxLifetime: 0.35,
    color: '#8b5e3c',
    glowColor: '#cc7733',
    damage: thornDamage * tuneOf('R10', 'dmgMultPerStack', 2.0) * stacks,
    hasDealtDamage: false,
    noRing: true,  // BETA 20.72 — burst kept as the damage carrier; circle hidden
  });
  // BETA 20.72 — R10 signature: fast light wooden thorn-spike spray with
  // red-hot tips lagging a few frames behind. Deliberately contrasted vs
  // R03 Wyfe Beater's few/heavy/slow iron chunks so the two never read alike.
  spawnParticles(_procParticles, x, y, '#8b5e3c', 10, _R10_THORN_SHARDS);
  _queueSpawn(0.05, x, y, '#ff3311', 6, _R10_REDHOT_TIPS);
}

/** Wyfe Beater (R03): cast-iron radial spikes.
 *  BETA 20.72 — VISUAL ONLY, no damage (R03 only adds p.thorns; the thorn
 *  damage applies in enemyAdapter). A pure-R03 build used to hit back
 *  completely invisibly — this gives it a face. Anti-R10 on purpose:
 *  FEWER / HEAVIER / SLOWER dark-iron chunks, red-hot tips trailing late. */
export function spawnWyfeBeaterThorns(x: number, y: number): void {
  spawnParticles(_procParticles, x, y, '#4f4038', 6, _R03_IRON_SPIKES);
  _queueSpawn(0.07, x, y, '#cc2200', 4, _R03_REDHOT_TIPS);
}

/** Bubble Wrap (R06): reflect 30% of damage + 10% chance invuln */
export function spawnBubbleWrapReflect(x: number, y: number, damage: number, stacks: number): void {
  // BETA 18.18 — FIFO cap shared across all _bursts producers.
  if (_bursts.length >= MAX_BURSTS) _bursts.shift();
  // Reflect burst — radial ring visual
  _bursts.push({
    x, y,
    maxRadius: 160,
    currentRadius: 0,
    lifetime: 0.30,
    maxLifetime: 0.30,
    color: '#88ddff',
    glowColor: '#44aaff',
    damage: damage * tuneOf('R06', 'reflectFracPerStack', 0.30) * stacks,  // BETA 21.81 — editable reflect fraction (× copies)
    hasDealtDamage: false,
    noRing: true,  // BETA 20.72 — burst kept as the damage carrier; circle hidden
  });
  // BETA 20.72 — R06 signature: rising bubbles that POP (was shared circle).
  // Wide size variance + negative gravity = floaty drift up; the local
  // bubble renderer bursts each one into flecks in its last 15% of life.
  // Single pale shock ring marks the reflect point.
  spawnParticles(_procParticles, x, y, '#88ddff', 6, _R06_BUBBLES);
  spawnParticles(_procParticles, x, y, '#bff0ff', 1, _R06_SHOCK_RING);
}

/** Bubble Wrap invuln: extend player invulnerability by 1 second */
export function triggerBubbleWrapInvuln(durationSec: number): void {
  _bubbleWrapInvulnTimer = Math.max(_bubbleWrapInvulnTimer, durationSec);
}

/** Returns remaining bubble-wrap invuln time (> 0 = invulnerable) */
export function getBubbleWrapInvulnTimer(): number { return _bubbleWrapInvulnTimer; }

// BETA 22.49 — Cursed Chat (R18) burst + stack-cache removed with the item.

// ─── Public: Enemy Feed (for homing / T-Bag targeting) ───────────────────────

/**
 * Feed live enemy positions each frame so rockets and the T-Bag can home.
 * Call from engine.ts after state.enemies is refreshed (after enemy module update).
 */
export function setActiveEnemies(enemies: { id: number | string; x: number; y: number; alive: boolean }[]): void {
  _enemyRefs = enemies;
}

// ─── Public: Anus Rocket (R08) ────────────────────────────────────────────────

/** Spawn a homing rocket from player position (on_kill, 15% chance). */
export function spawnAnusRocket(x: number, y: number, damage: number, stacks: number): void {
  const target = _nearestEnemy(x, y, null);
  const speed = 460;   // BETA 21.69 — 340 → 460 (Dylan: "moves kind of slow"). Snappier without overshooting (homing turn-rate caps the curve).
  let vx = 0, vy = 0, angle = 0;
  const targetId = target?.id ?? null;

  if (target) {
    const dx = target.x - x;
    const dy = target.y - y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    vx = (dx / dist) * speed;
    vy = (dy / dist) * speed;
    angle = Math.atan2(dy, dx);
  } else {
    angle = Math.random() * Math.PI * 2;
    vx = Math.cos(angle) * speed;
    vy = Math.sin(angle) * speed;
  }

  _rockets.push({ x, y, vx, vy, targetId, damage, stacks, lifetime: tuneOf('R08', 'rocketLifetime', 6), angle, speed });
  // BETA 17.08 — PSBOT cycle-30t Tier C #7: count rockets fired this run.
  _anusRocketsFiredThisRun++;
}

// BETA 17.08 — PSBOT cycle-30t Tier C #7: per-run anus rocket fire counter.
// Reset via resetAnusRocketTrace() at run start. Damage sum already covered
// via trackItemDmg('R08', ...) in enemyAdapter.ts:626 (sourceItemId='R08' path).
let _anusRocketsFiredThisRun = 0;
export function getAnusRocketsFiredThisRun(): number { return _anusRocketsFiredThisRun; }
export function resetAnusRocketTrace(): void { _anusRocketsFiredThisRun = 0; }

// ─── Public: Nutty T-Bag (R16) ───────────────────────────────────────────────

/**
 * Call every frame when player has R16 equipped.
 * Creates the T-Bag on first call; updates its damage/stacks and orbit position thereafter.
 */
export function updateTBagPosition(
  playerX: number, playerY: number,
  stacks: number, damage: number,
): void {
  if (!_tbag) {
    _tbag = {
      x: playerX, y: playerY,
      angle: 0,
      orbitRadius: 90,
      orbitSpeed: 1.8,
      state: 'orbit',
      dashTarget: null,
      dashCooldown: Math.max(tuneOf('R16', 'dashCdFloor', 0.8), tuneOf('R16', 'dashCdBase', 2.5) / stacks),
      dashTimer: 0,
      damage,
      stacks,
    };
    _tbagJustSpawned = true;
  } else {
    _tbag.damage = damage;
    _tbag.stacks = stacks;
    _tbag.dashCooldown = Math.max(tuneOf('R16', 'dashCdFloor', 0.8), tuneOf('R16', 'dashCdBase', 2.5) / stacks);
    if (_tbag.state === 'orbit') {
      _tbag.x = playerX + Math.cos(_tbag.angle) * _tbag.orbitRadius;
      _tbag.y = playerY + Math.sin(_tbag.angle) * _tbag.orbitRadius;
    } else if (_tbag.state === 'returning') {
      _tbag.x = playerX + Math.cos(_tbag.angle) * _tbag.orbitRadius;
      _tbag.y = playerY + Math.sin(_tbag.angle) * _tbag.orbitRadius;
      _tbag.state = 'orbit';
    }
  }
}

/** Returns true the first frame the T-Bag is created (so caller can play the summon sound). */
export function wasTBagJustSpawned(): boolean {
  const was = _tbagJustSpawned;
  _tbagJustSpawned = false;
  return was;
}
let _tbagJustSpawned = false;

/** Remove the T-Bag entity (called on run reset). */
export function clearTBag(): void { _tbag = null; }

// ─── Public: Queries ──────────────────────────────────────────────────────────

/** Returns true if the world position is inside any active poison cloud */
export function isPositionPoisoned(ex: number, ey: number): boolean {
  for (const cloud of _clouds) {
    const dx = ex - cloud.x;
    const dy = ey - cloud.y;
    if (dx * dx + dy * dy <= cloud.radius * cloud.radius) return true;
  }
  return false;
}

/**
 * Drain pending AoE damage requests.
 * Call once per frame from enemyAdapter.update() to apply burst/cloud damage.
 */
export function drainAoeDamageRequests(): AoeDamageRequest[] {
  const r = _aoeRequests;
  _aoeRequests = [];
  return r;
}

// ─── Public: Update ───────────────────────────────────────────────────────────

/**
 * Tick all active effects. Generates AoE damage requests for enemyAdapter.
 * Call once per frame from engine.ts BEFORE enemyAdapter.update() drains them.
 */
export function updateItemEffectVisuals(dt: number): void {
  // ── Bubble Wrap invuln timer ──
  if (_bubbleWrapInvulnTimer > 0) {
    _bubbleWrapInvulnTimer = Math.max(0, _bubbleWrapInvulnTimer - dt);
  }

  // ── Poison clouds (Diarrhea Gas) ──
  _clouds = _clouds.filter(cloud => {
    cloud.lifetime -= dt;
    if (cloud.lifetime <= 0) return false;

    cloud.tickTimer -= dt;
    if (cloud.tickTimer <= 0) {
      cloud.tickTimer += cloud.tickInterval;
      // Push one AoE request per tick — enemyAdapter applies damage
      _aoeRequests.push({
        x: cloud.x,
        y: cloud.y,
        radius: cloud.radius,
        damage: cloud.dps * cloud.tickInterval,
        sourceItemId: 'R11',
        // BETA 23.6x — CO-OP PHASE 2 part C (§3.3): owner stamp at enqueue
        // (machine-local; per-owner entities are part D). Same on every
        // _aoeRequests.push in this file.
        ownerPlayerId: _localOwnerPlayerId,
      });
    }
    return true;
  });

  // ── Anus Rockets (R08) ──
  const ROCKET_TURN = 2.8;      // radians/sec turn rate
  const ROCKET_HIT_R = 28;      // world units for hit detection

  // BETA 21.69 — age + cull the rocket smoke/fire VFX (the explosion fire outlives its rocket).
  for (let i = _rocketSmoke.length - 1; i >= 0; i--) { _rocketSmoke[i].age += dt; if (_rocketSmoke[i].age >= _rocketSmoke[i].max) _rocketSmoke.splice(i, 1); }
  for (let i = _rocketFires.length - 1; i >= 0; i--) { _rocketFires[i].age += dt; if (_rocketFires[i].age >= _rocketFires[i].max) _rocketFires.splice(i, 1); }
  for (let i = _rocketBlasts.length - 1; i >= 0; i--) { _rocketBlasts[i].age += dt; if (_rocketBlasts[i].age >= _rocketBlasts[i].max) _rocketBlasts.splice(i, 1); }

  _rockets = _rockets.filter(r => {
    r.lifetime -= dt;
    if (r.lifetime <= 0) {
      // Self-destruct: small explosion
      // BETA 17.95 — match the new sqrt-scaled blast radius (0.5× of the
      // on-hit blast since self-destruct is the consolation explosion).
      const selfBlast = (tuneOf('R08', 'blastBase', 80) + tuneOf('R08', 'blastPerSqrtStack', 20) * Math.sqrt(r.stacks)) * 0.5;
      _aoeRequests.push({ x: r.x, y: r.y, radius: selfBlast, damage: r.damage * tuneOf('R08', 'selfDestructDmgFrac', 0.35), sourceItemId: 'R08', ownerPlayerId: _localOwnerPlayerId });
      _rocketFires.push({ x: r.x, y: r.y, age: 0, max: 1.0, radius: Math.min(34, 16 + selfBlast * 0.28), seed: Math.random() * 6.283 });
      _spawnRocketBlast(r.x, r.y, Math.min(34, 16 + selfBlast * 0.28));
      return false;
    }

    // Re-target if current target is dead
    if (r.targetId) {
      const alive = _enemyRefs.some(e => e.id === r.targetId && e.alive);
      if (!alive) {
        const nt = _nearestEnemy(r.x, r.y, null);
        r.targetId = nt?.id ?? null;
      }
    }

    if (r.targetId) {
      const target = _enemyRefs.find(e => e.id === r.targetId && e.alive);
      if (target) {
        const dx = target.x - r.x;
        const dy = target.y - r.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < ROCKET_HIT_R) {
          // Hit!
          // BETA 17.95 — sqrt-scaled blast (was linear 80+20*stacks=2080px at 100 stacks).
          // BETA 17.96 — VFX further tightened: maxRadius 0.85× → 0.50× of gameplay
          // radius, lifetime 0.42s → 0.25s. Per Dylan: "VFX size increase on a
          // sprite projectile should not out-scale the actual gameplay element."
          // 0.5× means the visible ring sits well inside the damage zone, and
          // the shorter lifetime means less screen residue at high proc rates.
          const baseR = tuneOf('R08', 'blastBase', 80);
          const blastR = baseR + tuneOf('R08', 'blastPerSqrtStack', 20) * Math.sqrt(r.stacks);
          _aoeRequests.push({ x: r.x, y: r.y, radius: blastR, damage: r.damage, sourceItemId: 'R08', ownerPlayerId: _localOwnerPlayerId });
          // BETA 21.69 — Dylan: "that same cool fire effect" on the explosion. Ember-fire
          // burst where the rocket hits the enemy (lingers ~1.3s, fading).
          _rocketFires.push({ x: r.x, y: r.y, age: 0, max: 1.3, radius: Math.min(48, 22 + blastR * 0.28), seed: Math.random() * 6.283 });
          _spawnRocketBlast(r.x, r.y, Math.min(48, 22 + blastR * 0.28));
          // BETA 20.72 — R08 signature: backward-facing EXHAUST CONE (was a
          // generic orange circle, damage:0 pure visual). Two spark layers
          // (orange outer, hotter yellow inner) blast OPPOSITE the travel
          // direction. The exhaust opts are module consts; only .dir mutates
          // per hit (spawned synchronously, so no stagger-queue aliasing).
          const exDir = r.angle + Math.PI;
          _R08_EXHAUST_ORANGE.dir = exDir;
          _R08_EXHAUST_YELLOW.dir = exDir;
          spawnParticles(_procParticles, r.x, r.y, '#ff9933', 8, _R08_EXHAUST_ORANGE);
          spawnParticles(_procParticles, r.x, r.y, '#ffee66', 5, _R08_EXHAUST_YELLOW);
          // BETA 20.78 — ring dedup: the 3 staggered shock rings that fired
          // here duplicated the chick-magnet ring motif. Replaced with three
          // SMOKE DONUTS on the same 0 / 0.05 / 0.10s beat: gray-brown motes
          // placed around widening circles (lighter+tighter → darker+wider,
          // like rolling exhaust smoke), rising and drifting apart. Donut
          // offsets are positions, not opts — _R08_SMOKE_PUFF stays a static
          // const so the queued spawns are alias-safe. Exhaust cone above is
          // untouched.
          for (let pi = 0; pi < 5; pi++) {
            const pa = (pi * Math.PI * 2) / 5;
            spawnParticles(_procParticles, r.x + Math.cos(pa) * 9, r.y + Math.sin(pa) * 9, '#9b8b7b', 1, _R08_SMOKE_PUFF);
          }
          for (let pi = 0; pi < 4; pi++) {
            const pa = (pi * Math.PI * 2) / 4 + 0.45;
            _queueSpawn(0.05, r.x + Math.cos(pa) * 16, r.y + Math.sin(pa) * 16, '#857565', 1, _R08_SMOKE_PUFF);
          }
          for (let pi = 0; pi < 4; pi++) {
            const pa = (pi * Math.PI * 2) / 4 + 0.9;
            _queueSpawn(0.10, r.x + Math.cos(pa) * 23, r.y + Math.sin(pa) * 23, '#6f6155', 1, _R08_SMOKE_PUFF);
          }
          return false;
        }

        // Smooth homing: steer toward target
        const targetAngle = Math.atan2(dy, dx);
        let diff = targetAngle - r.angle;
        while (diff >  Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        const turn = Math.min(Math.abs(diff), ROCKET_TURN * dt) * Math.sign(diff);
        r.angle += turn;
        r.vx = Math.cos(r.angle) * r.speed;
        r.vy = Math.sin(r.angle) * r.speed;
      }
    }

    r.x += r.vx * dt;
    r.y += r.vy * dt;
    // BETA 21.69 — billow smoke from the tail (behind = opposite the travel direction).
    // BETA 21.96 — denser contrail (Dylan: "no smoke trail"): two puffs/frame at slightly
    // different tail offsets so the trail reads as a continuous plume, not dots.
    const _tailX = r.x - Math.cos(r.angle) * 14;
    const _tailY = r.y - Math.sin(r.angle) * 14;
    _spawnRocketSmoke(_tailX, _tailY);
    _spawnRocketSmoke(r.x - Math.cos(r.angle) * 22, r.y - Math.sin(r.angle) * 22);
    return true;
  });

  // ── Nutty T-Bag (R16) ──
  if (_tbag) {
    _tbag.angle    += _tbag.orbitSpeed * dt;
    _tbag.dashTimer += dt;

    if (_tbag.state === 'orbit' && _tbag.dashTimer >= _tbag.dashCooldown) {
      const target = _nearestEnemy(_tbag.x, _tbag.y, null);
      if (target) {
        const dx = target.x - _tbag.x;
        const dy = target.y - _tbag.y;
        if (dx * dx + dy * dy < 300 * 300) {
          _tbag.state = 'dash';
          _tbag.dashTarget = { x: target.x, y: target.y };
          _tbag.dashTimer = 0;
        }
      }
    }

    if (_tbag.state === 'dash' && _tbag.dashTarget) {
      const dx = _tbag.dashTarget.x - _tbag.x;
      const dy = _tbag.dashTarget.y - _tbag.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 22) {
        const blastR = tuneOf('R16', 'blastBase', 55) + _tbag.stacks * tuneOf('R16', 'blastPerStack', 15);
        _aoeRequests.push({ x: _tbag.x, y: _tbag.y, radius: blastR, damage: _tbag.damage, sourceItemId: 'R16', ownerPlayerId: _localOwnerPlayerId });
        // BETA 20.72 — R16 signature: DIVE-CRATER (was literally the same
        // brown circle as R10 Morning Wood — worst twin in the file; the
        // push was damage:0 pure visual, so fully replaced). Dark center
        // puff + dirt chunks that arc up and FALL (heavy gravity) + a
        // ground shock ring where the bag slammed down.
        spawnParticles(_procParticles, _tbag.x, _tbag.y, '#3d2a1a', 6, _R16_CRATER_CENTER);
        spawnParticles(_procParticles, _tbag.x, _tbag.y, '#6b4a2f', 5, _R16_DIRT_CHUNKS);
        spawnParticles(_procParticles, _tbag.x, _tbag.y, '#8b5e3c', 1, _R16_GROUND_RING);
        _tbag.state = 'returning';
        _tbag.dashTarget = null;
      } else {
        const DASH_SPEED = 480;
        _tbag.x += (dx / dist) * DASH_SPEED * dt;
        _tbag.y += (dy / dist) * DASH_SPEED * dt;
      }
    }
  }

  // ── Burst effects (explosions, XP bursts, reflections) ──
  _bursts = _bursts.filter(burst => {
    burst.lifetime -= dt;
    if (burst.lifetime <= 0) return false;

    const progress = 1 - burst.lifetime / burst.maxLifetime;
    burst.currentRadius = burst.maxRadius * progress;

    // One-shot damage on first frame
    if (!burst.hasDealtDamage && burst.damage > 0) {
      burst.hasDealtDamage = true;
      _aoeRequests.push({
        x: burst.x,
        y: burst.y,
        radius: burst.maxRadius,
        damage: burst.damage,
        sourceItemId: 'R17',   // BETA 21.78 — attribute NickMerc splash damage in telemetry (was blank)
        ownerPlayerId: _localOwnerPlayerId,
      });
    }
    return true;
  });

  // ── BETA 20.72 — proc-signature particles ──
  // Tick the stagger queue: sub-bursts (thorn tips, ripple waves, glitch
  // strobes, exhaust rings) fire a few frames after the main burst.
  // In-place write-index compaction — no per-frame allocations.
  let _wq = 0;
  for (let i = 0; i < _pendingSpawns.length; i++) {
    const ps = _pendingSpawns[i];
    ps.delay -= dt;
    if (ps.delay <= 0) {
      spawnParticles(_procParticles, ps.x, ps.y, ps.color, ps.count, ps.opts);
    } else {
      _pendingSpawns[_wq++] = ps;
    }
  }
  _pendingSpawns.length = _wq;

  // Particle physics — exact mirror of the engine's state.particles motion
  // (gravity → vy, pos += v·dt, drag default 0.95, spin, life) so proc
  // particles behave identically to every other VFX in the game.
  let _wp = 0;
  for (let i = 0; i < _procParticles.length; i++) {
    const p = _procParticles[i];
    if (p.gravity) p.vy += p.gravity * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    const _drag = p.drag ?? 0.95;
    p.vx *= _drag;
    p.vy *= _drag;
    if (p.spin) p.angle = (p.angle ?? 0) + p.spin * dt;
    p.life -= dt;
    if (p.life > 0) _procParticles[_wp++] = p;
  }
  _procParticles.length = _wp;
}

// ─── Public: Reset ────────────────────────────────────────────────────────────

export function resetItemEffects(): void {
  _clouds = [];
  _bursts = [];
  _aoeRequests = [];
  _bubbleWrapInvulnTimer = 0;
  _rockets = [];
  _rocketSmoke = [];
  _rocketFires = [];
  _rocketBlasts = [];
  _tbag = null;
  _enemyRefs = [];
  // BETA 20.72 — proc-signature particle harness
  _procParticles = [];
  _pendingSpawns = [];
}

// ─── Public: Render ───────────────────────────────────────────────────────────

/**
 * BETA 13.102 — Render the floor-level item effects (poison clouds) UNDER
 * enemies/bots/character. Engine calls this BEFORE renderEnemies. User feedback
 * on Diarrhea Gas: VFX should sit "under enemies, under bot characters and
 * regular character enemies, but above rooms".
 */
// BETA 17.96 — Lazy import to avoid circular dep risk: read isVfxEnabled at
// call time. Module-level alias so the renderer hot path doesn't pay an
// import lookup per frame.
import { isVfxEnabled as _isVfxEnabled } from './debugState';

export function renderItemEffectsBelow(
  ctx: CanvasRenderingContext2D,
  camX: number, camY: number,
  canvasW: number, canvasH: number,
  zoom: number,
): void {
  // BETA 17.96 — VFX toggle.
  if (!_isVfxEnabled()) return;
  const offX = canvasW / 2 - camX * zoom;
  const offY = canvasH / 2 - camY * zoom;

  // ── Poison clouds (Diarrhea Gas) ──
  // BETA 14.78 — sprite-based ground render. User: "this is a ground sprite
  // to replace the vfx. duplicate this sprite to go through 3 of these and
  // just rotate each sprite so when it flickers through it looks animated.
  // put the sprite at 30% opacity." Three rotation phases (0° / 120° / 240°)
  // cycled at ~6 fps to look animated. Falls back to old gradient if sprite
  // not loaded yet.
  const gasImg = _diarrheaGasImg;
  const gasReady = gasImg && gasImg.complete && gasImg.naturalWidth > 0;
  for (const cloud of _clouds) {
    const sx = cloud.x * zoom + offX;
    const sy = cloud.y * zoom + offY;
    const sr = cloud.radius * zoom;
    const ageFactor = cloud.lifetime / cloud.maxLifetime;

    ctx.save();
    if (gasReady) {
      // BETA 14.78 — sprite-based render (per user spec)
      // 3-frame rotation animation @ ~6 fps. Anchored on cloud position.
      const phase = Math.floor(performance.now() / 167) % 3;       // ~6 fps cycle
      const rotation = (phase * 2 * Math.PI) / 3;                  // 0 / 120 / 240
      const fadeAlpha = Math.min(1, ageFactor * 1.6);              // fade out toward end
      ctx.globalAlpha = 0.30 * fadeAlpha;                          // user spec: 30% opacity
      ctx.translate(sx, sy);
      ctx.rotate(rotation);
      // Render sprite to fill the cloud diameter (2 * radius). The sprite is
      // square so we draw it sized to (2*sr) × (2*sr) centered on cloud origin.
      ctx.drawImage(gasImg, -sr, -sr, sr * 2, sr * 2);
    } else {
      // Fallback: original radial-gradient render (works if sprite hasn't loaded)
      const alpha = Math.min(0.55, ageFactor * 0.5 + 0.15);
      const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
      grad.addColorStop(0,   `rgba(80,  220, 40, ${alpha})`);
      grad.addColorStop(0.55,`rgba(40,  160, 20, ${alpha * 0.7})`);
      grad.addColorStop(1,   `rgba(10,  60,  5,  0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

/**
 * Render the on-top item effects (impact bursts + flying rocket sprites).
 * Engine calls this AFTER renderEnemies — these need to be visible over the
 * action (explosion rings + rocket trails).
 */
export function renderItemEffects(
  ctx: CanvasRenderingContext2D,
  camX: number, camY: number,
  canvasW: number, canvasH: number,
  zoom: number,
): void {
  // BETA 17.96 — VFX toggle skips the DECORATION layers (bursts, proc
  // particles, trails, afterimages, screen tints).
  // BETA 20.75 — but NOT the rocket / T-bag SPRITES. Those are GAMEPLAY
  // OBJECTS (they deal damage where they are); with the old whole-function
  // early-return, a player with VFX off fought invisible rockets and an
  // invisible bag (Dylan: "i dont see nutty t bag... my rockets i dont see
  // either"). The lab checkbox persists in localStorage, so one lab visit
  // could silently hide them forever. Sprites now ALWAYS draw; only the
  // decoration around them respects the toggle.
  const _vfxOn = _isVfxEnabled();
  const offX = canvasW / 2 - camX * zoom;
  const offY = canvasH / 2 - camY * zoom;

  // BETA 13.102 — poison cloud loop relocated to renderItemEffectsBelow
  // (engine.ts now calls Below before renderEnemies, this Above after).
  // Bursts + rockets stay here so explosion rings + rocket trails appear
  // ON TOP of enemies (combat feedback over the action).

  // ── Burst effects ── (decoration — respects the VFX toggle)
  for (const burst of (_vfxOn ? _bursts : [])) {
    // BETA 20.72 — damage-carrier bursts (noRing) keep their one-shot AoE in
    // updateItemEffectVisuals but draw NOTHING here — each proc's particle
    // signature replaced the shared circle (Dylan: "buncha bullshit items
    // ... just do circles as a hit").
    if (burst.noRing) continue;
    if (burst.currentRadius <= 2) continue;
    const sx = burst.x * zoom + offX;
    const sy = burst.y * zoom + offY;
    const sr = burst.currentRadius * zoom;
    const progress = 1 - burst.lifetime / burst.maxLifetime;
    const alpha = Math.max(0, (1 - progress) * 0.85);
    const thickness = Math.max(1, (1 - progress) * 5 * zoom);

    ctx.save();
    ctx.strokeStyle = burst.color;
    ctx.lineWidth = thickness;
    ctx.globalAlpha = alpha;
    // BETA 20.72 — shadowBlur removed (perf law since 13.70)
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.stroke();

    // Inner fill (faint)
    ctx.globalAlpha = alpha * 0.18;
    ctx.fillStyle = burst.color;
    ctx.fill();
    ctx.restore();
  }

  // ── BETA 20.72 — proc-signature particles ──
  // Compact mirror of the engine's 20.70 shape switch (engine.ts step 9) —
  // local copy because these must draw ON TOP of enemies (engine's
  // state.particles pass renders UNDER them) and this renderer works in
  // screen space (world→screen per particle, sizes scaled by zoom).
  // No shadowBlur. One bespoke addition: bubbles POP into flecks at the
  // end of life (R06 Bubble Wrap).
  for (const p of (_vfxOn ? _procParticles : [])) {
    const px = p.x * zoom + offX;
    const py = p.y * zoom + offY;
    if (px < -40 || px > canvasW + 40 || py < -40 || py > canvasH + 40) continue;
    const lifeT = Math.max(0, p.life / p.maxLife);
    const fade = Math.pow(lifeT, p.fadePow ?? 2);
    // BETA 20.77 — PERF: same restructure as the engine particle pass —
    // skip near-invisible particles, no per-particle save/restore (only
    // shard/star transform and save/restore locally), one reset after.
    if (fade < 0.03 && p.shape !== 'bubble') continue; // bubbles POP at low lifeT — keep them
    const sz = Math.max(0.5, p.size * (0.4 + 0.75 * lifeT)) * zoom;
    ctx.fillStyle = p.color;
    ctx.strokeStyle = p.color;
    switch (p.shape) {
      case 'spark': {
        // thin bright streak along travel — exhaust / knowledge rays
        const a = p.angle ?? Math.atan2(p.vy, p.vx);
        const len = sz * (p.stretch ?? 4);
        ctx.globalAlpha = fade;
        ctx.lineWidth = Math.max(1, sz * 0.55);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(px - Math.cos(a) * len, py - Math.sin(a) * len);
        ctx.lineTo(px + Math.cos(a) * len * 0.25, py + Math.sin(a) * len * 0.25);
        ctx.stroke();
        break;
      }
      case 'shard': {
        // spinning angular splinter — thorns / iron spikes / dirt / glitch
        ctx.globalAlpha = fade;
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(p.angle ?? 0);
        ctx.beginPath();
        ctx.moveTo(0, -sz * 1.5);
        ctx.lineTo(sz * 0.72, sz);
        ctx.lineTo(-sz * 0.72, sz);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }
      case 'ring': {
        // expanding hollow outline — shockwaves
        const r = sz * (1 + (1 - lifeT) * 3.6);
        ctx.globalAlpha = fade * 0.9;
        ctx.lineWidth = Math.max(1, sz * 0.5 * lifeT);
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'mote': {
        // soft flickering dot — crater dust / glitch static
        const flick = 0.62 + 0.38 * Math.sin(p.life * 28 + p.x * 0.5);
        ctx.globalAlpha = fade * 0.85 * flick;
        ctx.beginPath();
        ctx.arc(px, py, Math.max(0.5, p.size * (0.6 + 0.4 * lifeT) * zoom), 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'bubble': {
        if (lifeT < 0.15) {
          // POP — last 15% of life: the bubble bursts into 3 tiny flecks
          // flying outward. Deterministic per-particle seed (no Math.random
          // in the render loop) so the pop doesn't jitter frame-to-frame.
          const seed = p.x * 0.7 + p.y * 0.3;
          const fr = sz * (1.3 + (0.15 - lifeT) * 8);  // flecks fly out as life ends
          const fs = Math.max(0.6, sz * 0.25);
          ctx.globalAlpha = Math.min(1, 0.9 * (lifeT / 0.15) + 0.1);
          for (let f = 0; f < 3; f++) {
            const fa = seed + f * 2.094;  // 3 flecks, 120° apart
            ctx.beginPath();
            ctx.arc(px + Math.cos(fa) * fr, py + Math.sin(fa) * fr, fs, 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          // hollow bubble + white-ish highlight dot (rises on -gravity)
          ctx.globalAlpha = fade * 0.85;
          ctx.lineWidth = Math.max(1, sz * 0.32);
          ctx.beginPath();
          ctx.arc(px, py, sz, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = fade;
          ctx.beginPath();
          ctx.arc(px - sz * 0.32, py - sz * 0.32, Math.max(0.5, sz * 0.22), 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case 'star': {
        // 4-point sparkle — Big Brain knowledge energy
        ctx.globalAlpha = fade;
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(p.angle ?? 0);
        const sl = sz * 1.9, sw = sz * 0.32;
        ctx.beginPath();
        ctx.moveTo(0, -sl); ctx.lineTo(sw, 0); ctx.lineTo(0, sl); ctx.lineTo(-sw, 0); ctx.closePath();
        ctx.moveTo(-sl, 0); ctx.lineTo(0, sw); ctx.lineTo(sl, 0); ctx.lineTo(0, -sw); ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }
      default: {
        // plain circle — R17 splash droplets
        ctx.globalAlpha = fade;
        ctx.beginPath();
        ctx.arc(px, py, sz, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  // BETA 20.77 — single state reset for the proc-particle pass.
  ctx.globalAlpha = 1;
  ctx.lineCap = 'butt';
  ctx.lineWidth = 1;

  // ── Anus Rockets ──
  // The sprite's nose naturally points upper-left (-135° = -3π/4).
  // Rotation offset corrects so the nose faces the travel direction.
  // BETA 21.69 — the sprite's nose points LEFT (west = angle π), so to face the travel
  // direction (r.angle) the sprite must rotate by r.angle + π. Was 3π/4 (≈135° off — the
  // rocket flew sideways). Now it points where it's going.
  const ROCKET_SPRITE_OFFSET = Math.PI;
  const ROCKET_DRAW = 54 * zoom;
  const ROCKET_HALF = ROCKET_DRAW / 2;

  // BETA 21.69 — smoke trail (drawn first, behind the rockets) + explosion ember-fire.
  if (_vfxOn) {
    for (const s of _rocketSmoke) {
      const t = s.age / s.max;
      const ssx = (s.x + s.vx * s.age) * zoom + offX;
      const ssy = (s.y + s.vy * s.age) * zoom + offY;
      const rr = (s.r0 + t * 18) * zoom;
      const a = (1 - t) * (1 - t) * 0.5;           // ease-out fade
      const g = Math.round(s.gray - t * 70);        // darkens as it dissipates
      ctx.globalAlpha = a;
      ctx.fillStyle = `rgb(${g},${g},${g})`;
      ctx.beginPath(); ctx.arc(ssx, ssy, rr, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    const _fireTime = performance.now() / 1000;
    for (const f of _rocketFires) {
      const t = f.age / f.max;
      _drawRocketFire(ctx, f.x * zoom + offX, f.y * zoom + offY, f.radius * zoom * (0.65 + 0.35 * (1 - t)), (1 - t), _fireTime, f.seed);
    }
    // BETA 21.96 — bespoke explosion: flash + shockwave ring + flying sparks.
    for (const b of _rocketBlasts) {
      const t = b.age / b.max;                       // 0..1
      const bx = b.x * zoom + offX, by = b.y * zoom + offY;
      // (1) bright flash — white→yellow core, gone by ~40%
      if (t < 0.42) {
        const ft = t / 0.42;
        ctx.globalAlpha = (1 - ft) * 0.92;
        ctx.fillStyle = `rgb(255,${Math.round(238 - ft * 70)},${Math.round(170 - ft * 150)})`;
        ctx.beginPath(); ctx.arc(bx, by, b.radius * zoom * (0.45 + ft * 0.55), 0, Math.PI * 2); ctx.fill();
      }
      // (2) shockwave ring — expands out + thins/fades
      ctx.globalAlpha = (1 - t) * 0.85;
      ctx.strokeStyle = `rgba(255,${Math.round(150 + t * 70)},40,1)`;
      ctx.lineWidth = (2.5 + (1 - t) * 4) * zoom;
      ctx.beginPath(); ctx.arc(bx, by, b.radius * zoom * (0.35 + t * 1.15), 0, Math.PI * 2); ctx.stroke();
      // (3) ember sparks flung outward (yellow→orange streaks)
      ctx.lineCap = 'round';
      ctx.globalAlpha = (1 - t);
      ctx.strokeStyle = t < 0.5 ? '#ffe066' : '#ff7a18';
      ctx.lineWidth = 2.2 * zoom;
      for (const s of b.sparks) {
        const d = s.sp * zoom * (t * (2 - t));        // ease-out outward
        const hx = bx + Math.cos(s.ang) * d, hy = by + Math.sin(s.ang) * d;
        ctx.beginPath(); ctx.moveTo(hx, hy);
        ctx.lineTo(hx - Math.cos(s.ang) * s.len * zoom, hy - Math.sin(s.ang) * s.len * zoom); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }

  for (const r of _rockets) {
    const sx = r.x * zoom + offX;
    const sy = r.y * zoom + offY;

    // Exhaust flame trail (drawn first, behind sprite) — decoration, gated.
    if (_vfxOn) {
      const trailAngle = r.angle + Math.PI;
      const trailLen   = (18 + Math.random() * 10) * zoom;
      const trailX     = Math.cos(trailAngle) * trailLen;
      const trailY     = Math.sin(trailAngle) * trailLen;
      ctx.save();
      const trailGrad = ctx.createLinearGradient(sx, sy, sx + trailX, sy + trailY);
      trailGrad.addColorStop(0,   'rgba(255, 120, 30, 0.95)');
      trailGrad.addColorStop(0.5, 'rgba(255,  50,  0, 0.55)');
      trailGrad.addColorStop(1,   'rgba(255,   0,  0, 0)');
      ctx.strokeStyle = trailGrad;
      ctx.lineWidth   = 4 * zoom;
      ctx.lineCap     = 'round';
      // BETA 20.72 — shadowBlur removed (perf law since 13.70)
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx + trailX, sy + trailY);
      ctx.stroke();
      ctx.restore();
    }

    // Sprite (rotated to face travel direction)
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(r.angle + ROCKET_SPRITE_OFFSET);
    if (_rocketImg && _rocketImg.complete && _rocketImg.naturalWidth > 0) {
      ctx.drawImage(_rocketImg, -ROCKET_HALF, -ROCKET_HALF, ROCKET_DRAW, ROCKET_DRAW);
    } else {
      // Fallback: orange circle
      // BETA 20.72 — shadowBlur removed (perf law since 13.70)
      ctx.fillStyle = '#ff6600';
      ctx.beginPath();
      ctx.arc(0, 0, 10 * zoom, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // ── Nutty T-Bag ──
  if (_tbag) {
    const sx = _tbag.x * zoom + offX;
    const sy = _tbag.y * zoom + offY;
    const TBAG_DRAW = 60 * zoom;
    const TBAG_HALF = TBAG_DRAW / 2;
    const bob = Math.sin(performance.now() * 0.0038) * 3 * zoom;

    ctx.save();
    ctx.translate(sx, sy + bob);

    if (_tbagImg && _tbagImg.complete && _tbagImg.naturalWidth > 0) {
      // BETA 20.72 — R16 dive MOTION-BLUR: two afterimage copies trail
      // BACKWARD along the dash direction (replaces the old shadowBlur
      // glow — perf law since 13.70, and a streak reads as speed where a
      // glow read as nothing). Skipped gracefully when sprite not loaded.
      if (_vfxOn && _tbag.state === 'dash' && _tbag.dashTarget) {
        const ddx = _tbag.dashTarget.x - _tbag.x;
        const ddy = _tbag.dashTarget.y - _tbag.y;
        const dlen = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
        const nbx = -(ddx / dlen);  // backward unit vector (screen-space px)
        const nby = -(ddy / dlen);
        ctx.globalAlpha = 0.12;
        ctx.drawImage(_tbagImg, -TBAG_HALF + nbx * 24, -TBAG_HALF + nby * 24, TBAG_DRAW, TBAG_DRAW);
        ctx.globalAlpha = 0.25;
        ctx.drawImage(_tbagImg, -TBAG_HALF + nbx * 12, -TBAG_HALF + nby * 12, TBAG_DRAW, TBAG_DRAW);
        ctx.globalAlpha = 1;
      }
      ctx.drawImage(_tbagImg, -TBAG_HALF, -TBAG_HALF, TBAG_DRAW, TBAG_DRAW);
    } else {
      ctx.fillStyle = '#8b5e3c';
      ctx.fillRect(-20 * zoom, -20 * zoom, 40 * zoom, 40 * zoom);
    }
    ctx.restore();
  }

  // ── Bubble Wrap invuln flash (screen-space tint, drawn regardless of camera) ──
  if (_vfxOn && _bubbleWrapInvulnTimer > 0) {
    const flashAlpha = Math.min(0.18, _bubbleWrapInvulnTimer * 0.25);
    ctx.save();
    ctx.globalAlpha = flashAlpha;
    ctx.fillStyle = '#88ccff';
    ctx.fillRect(0, 0, canvasW, canvasH);
    ctx.restore();
  }
}
